import Backbone from 'backbone'
import _ from 'lodash'
import calendars from 'scoreboard/components/calendars'
import formatValue from 'scoreboard/components/format-value'
import i18n from 'scoreboard/components/i18n'
import highlight from 'scoreboard/components/highlight'
import template from './initiative-impact-dialog.hbs'
import ScorecardNode from 'scoreboard/scorecards/scorecard-node'
const { metricish } = ScorecardNode
import impactIconId from './impact-icon-id'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER } = DataType

const InitiativeImpactDialog = Backbone.View.extend({
	className: 'initiative-impact-dialog',

	/**
	 * @param {Object} options
	 * @param {Object} options.calculations the InitiativeImpactCalculations for
	 * the scorecard item and initiative
	 */
	initialize: function (options) {
		this.calculations = options.calculations
		this.scorecardItem = options.calculations.scorecardItem
		this.initiative = options.calculations.initiative
	},

	render: function () {
		this.$el.empty().append(template(this.context()))

		let impact = this.calculations.impact

		this.$('.actual-performance-header, .actual-performance-impact, .actual-performance-details, '
				+ '.trend-header, .trend-impact, .trend-details')
			.toggleClass('is-not-displayed', impact === 'insufficient-data')
		this.$('.errors').toggleClass('is-not-displayed', impact !== 'insufficient-data')

		return this
	},

	context: function () {
		let impact = this.calculations.impact
		let initiativeType = this.initiative.type

		return {
			dialogHeader: impactToMessage(impact, initiativeType),
			overallSectionHeader: impactToMessage(impact, null, 'header'),
			errors: this.calculations.errorMessages,
			overallParagraph: this.overallParagraph(),
			overallIcon: impactIconId(impact, true) + '-large',
			actualPerformanceIcon: impactIconId(_.get(this.calculations, 'valueImpact.impact')),
			actualPerformanceImpactMessage: this.actualPerformanceImpactMessage(),
			actualPerformanceParagraph: this.actualPerformanceParagraph(),
			actualPerformanceDetailsParagraph: this.actualPerformanceDetailsParagraph(),
			trendIcon: impactIconId(_.get(this.calculations, 'slopeImpact.impact')),
			trendImpactMessage: this.trendImpactMessage(),
			trendParagraph: this.trendParagraph(),
			trendDetailsParagraph: this.trendDetailsParagraph()
		}
	},

	overallParagraph: function () {
		let impact = this.calculations.impact

		if (impact === 'insufficient-data') {
			// Example: There's currently {not enough data} to determine whether the
			// {Migrate Servers to Cloud} {initiative} is having an impact on the
			// {Improve IT Effectiveness} {objective}.
			return i18n.get('initiatives.impact.overall.description.insufficientData',
				impactToMessage(impact),
				this.initiativeLabel(true),
				this.initiativeTypeMessage(),
				this.scorecardItemLabel(true),
				this.scorecardItemTypeMessage())
		} else {
			// Example: The {Migrate Servers to Cloud} {initiative} is having
			// {an overall positive impact} on the {Improve IT Effectiveness} {objective}.
			return i18n.get('initiatives.impact.overall.description',
				this.initiativeLabel(true),
				this.initiativeTypeMessage(),
				impactToMessage(impact, null, 'overall'),
				this.scorecardItemLabel(true),
				this.scorecardItemTypeMessage())
		}
	},

	actualPerformanceImpactMessage: function () {
		let impact = _.get(this.calculations, 'valueImpact.impact')
		return impact ? impactToMessage(impact) : ''
	},

	actualPerformanceParagraph: function () {
		let calcs = this.calculations
		let valueCalcs = calcs.valueImpact

		if (!valueCalcs) {
			return null
		}

		let impact = valueCalcs.impact
		let sn = this.scorecardItem

		// Example: Before the {initiative} started, {Improve IT Effectiveness} was projected to have a {score} of {8.3} in {September 2018}.
		let beforeSentence = i18n.get('initiatives.impact.actualPerformance.before',
			this.initiativeLabel(),
			this.initiativeTypeMessage(),
			this.scorecardItemLabel(),
			this.scoreOrValueMessage(),
			this.formattedScoreOrValue(valueCalcs.before),
			calendars.periodsById[calcs.duringRange.endPeriodId].label)

		let complete = !!this.initiative.aggregatedCompletionDate

		// Example: That {score} is actually trending at {8.4}
		let actualSentenceKey = 'initiatives.impact.actualPerformance.actuallyTrending.'
			+ (complete ? 'complete' : 'inProgress')
		let actualSentence = i18n.get(actualSentenceKey,
			this.scoreOrValueMessage(),
			this.formattedScoreOrValue(valueCalcs.during))

		let differenceSentence = this.largeOrSmallEnoughDifferenceSentence(impact,
			'initiatives.impact.actualPerformance')

		return `${beforeSentence} ${actualSentence} ${differenceSentence}`
	},

	actualPerformanceDetailsParagraph: function () {
		let valueCalcs = this.calculations.valueImpact

		if (!valueCalcs) {
			return null
		}

		let insideOrOutside = valueCalcs.impact === 'none' ? 'inside' : 'outside'
		let delta = Math.abs(valueCalcs.before - valueCalcs.during)

		// Example: The {score} difference of {0.1} is not statistically significant because it’s inside the trend lines' 95% confidence interval of {0.2}.
		return i18n.get(`initiatives.impact.actualPerformance.${insideOrOutside}ConfidenceInterval`,
			this.scoreOrValueMessage(),
			this.formattedScoreOrValue(delta),
			this.formattedScoreOrValue(valueCalcs.combinedConfidenceInterval))
	},

	trendImpactMessage: function () {
		let impact = _.get(this.calculations, 'slopeImpact.impact')
		return impact ? impactToMessage(impact) : ''
	},

	trendParagraph: function () {
		let calcs = this.calculations
		let trendCalcs = calcs.slopeImpact

		if (!trendCalcs) {
			return null
		}

		let impact = trendCalcs.impact
		let sn = this.scorecardItem

		let changeSentence = ({ periodRange, slope, before }) => {
			let calendar = calendars.periodsById[periodRange.startPeriodId].calendar
			let distance = calendars.distance(
				periodRange.startPeriodId, periodRange.endPeriodId) + 1
			let periodCount = calendars.periodCount(calendar.id, distance)

			let increasedOrDecreased = i18n.get('initiatives.impact.' +
				(slope > 0 ? 'increased' : 'decreased'))

			let periodSlope = this.formattedScoreOrValue(
				Math.abs(slope * calendar.avgPeriodSize))

			if (before) {
				// Example: In the {4 months} before the {Migrate Servers to Cloud} {initiative} started,
				// the {Improve IT Effectiveness} {objective} {score} {decreased} by a {monthly} average of {0.3}.
				return i18n.get('initiatives.impact.performanceTrend.before',
					periodCount,
					this.initiativeLabel(),
					this.initiativeTypeMessage(),
					this.scorecardItemLabel(),
					this.scorecardItemTypeMessage(),
					this.scoreOrValueMessage(),
					increasedOrDecreased,
					calendar.label.toLowerCase(),
					periodSlope)
			} else {
				// Example: In the {4 months} after the {initiative} started,
				// the {objective} {score} {decreased} by a {monthly} average of {0.3}.
				return i18n.get('initiatives.impact.performanceTrend.after',
					periodCount,
					this.initiativeTypeMessage(),
					this.scorecardItemTypeMessage(),
					this.scoreOrValueMessage(),
					increasedOrDecreased,
					calendar.label.toLowerCase(),
					periodSlope)
			}
		}

		let differenceSentence = this.largeOrSmallEnoughDifferenceSentence(impact,
			'initiatives.impact.performanceTrend')

		return changeSentence({ periodRange: calcs.beforeRange, slope: trendCalcs.before, before: true }) +
			' ' + changeSentence({ periodRange: calcs.duringRange, slope: trendCalcs.during, before: false }) +
			' ' + differenceSentence
	},

	largeOrSmallEnoughDifferenceSentence: function (impact, actualOrTrend) {
		let differenceSentenceKey = 'initiatives.impact.' +
			(impact === 'none' ? 'smallEnoughDifference' : 'largeEnoughDifference')
		let correlationKey = impact === 'none'? 'correlation': impact + 'Correlation'
		return impact === 'none'?
			i18n.get(differenceSentenceKey,
				impactToMessage(correlationKey),
				i18n.get(actualOrTrend)) :
			i18n.get(differenceSentenceKey,
				impactToMessage(correlationKey),
				i18n.get(actualOrTrend),
				this.initiativeTypeMessage(),
				this.scorecardItemTypeMessage())
	},

	trendDetailsParagraph: function () {
		let trendCalcs = this.calculations.slopeImpact
		if (!trendCalcs) {
			return null
		}

		let pValue = formatValue({
			value: trendCalcs.pValue,
			dataType: NUMBER,
			precision: 3,
			forcePrecision: true
		})

		// Example: Comparing the {objective} {score} regressions from before the {initiative} started
		// and after the {initiative} started results in a p-value of {0.426}.
		let pValueSentence = i18n.get('initiatives.impact.performanceTrend.pvalue',
			this.scorecardItemTypeMessage(),
			this.scoreOrValueMessage(),
			this.initiativeTypeMessage(),
			pValue)

		let aboveOrBelow = trendCalcs.impact === 'none'
			? 'above' : 'below'

		let significanceSentence = i18n.get(
			`initiatives.impact.performanceTrend.${aboveOrBelow}SignificanceLevel`)

		return pValueSentence + ' ' + significanceSentence
	},

	scoreOrValueMessage: function () {
		let text = i18n.get(metricish(this.scorecardItem)
			? 'common.value' : 'common.score')

		return `<span class='score-or-value'>${text}</span>`
	},

	formattedScoreOrValue: function (raw) {
		let sn = this.scorecardItem
		if (metricish(sn)) {
			return formatValue({
				value: raw,
				dataType: sn.dataType,
				precision: sn.precision,
				currencyCode: sn.currencyCode
			})
		} else {
			return formatValue({
				value: raw,
				dataType: NUMBER
				// Per https://github.com/SpiderStrategies/Scoreboard/issues/5284
				// scores use the database's configured precision, not the metric's
			})
		}
	},

	scorecardItemLabel: function (isLink) {
		let url = `/scorecards/${this.scorecardItem.id}`
		let label =  isLink ?
			`<span class="text-link is-inlined sp-navigate" href="${url}">${_.escape(this.scorecardItem.label)}</span>` :
			highlight(this.scorecardItem.label)
		return label
	},

	scorecardItemTypeMessage: function () {
		let suffix = this.scorecardItem.type === 'root'
			? 'scorecardRoot' : this.scorecardItem.type
		let message = i18n.get('scorecards.node.' + suffix)
		return message === message.toUpperCase()? message : message.toLowerCase()
	},

	initiativeLabel: function (isLink) {
		let url = `/initiatives/${this.initiative.id}`
		let label =  isLink ?
			`<span class="text-link is-inlined sp-navigate" href="${url}">${_.escape(this.initiative.label)}</span>` :
			highlight(this.initiative.label)
		return label
	},

	initiativeTypeMessage: function () {
		return i18n.get('initiatives.type.' + this.initiative.type).toLowerCase()
	}
})

function impactToMessage (impact, initiativeLabel, type = 'simple') {
	let prefix = 'initiatives.impact.'
	if (type === 'overall') {
		prefix += 'overall.'
	} else if (type === 'header') {
		prefix += 'header.'
	}
	if (initiativeLabel && impact !== 'insufficient-data') {
		// Use i18n to apply configurable terminology changes to the
		// initiative label #55589
		let configuredLabel = i18n.get(initiativeLabel)
		return i18n.get(prefix + 'type.' + _.camelCase(impact), configuredLabel)
	} else {
		return i18n.get(prefix + _.camelCase(impact))
	}
}

export default InitiativeImpactDialog
export { impactToMessage }

InitiativeImpactDialog.__filename__ = 'related-items/initiative-impact-dialog.js'
